import { usePrincipalKey } from "@auth/sessionStore";
import { UserMenu } from "@components/UserMenu";
import { useUser } from "@context/UserContext";
import { CallBubble } from "@notifications/CallBubble";
import { AnsweredCallNavigation } from "@phone/AnsweredCallNavigation";
import { DisplayClock } from "@scribenote/brian-design-system";
import { BrianBubbleProvider } from "@shell/brianBubble";
import { BrianCorner } from "@shell/BrianCorner";
import { FrontDeskRedirect } from "@shell/FrontDeskRedirect";
import { GlobalControls } from "@shell/GlobalControls";
import { SearchField, type SearchFieldHandle } from "@shell/SearchField";
import { WorkstationNav } from "@shell/WorkstationNav";
import { Outlet } from "@tanstack/react-router";
import { FailedUploadsBanner } from "@transcription/FailedUploadsBanner";
import { isFrontDeskUser } from "@users/userClient";
import { useRef, useState } from "react";

import { QuestionsBubble } from "../questions/QuestionsBubble";

/** The page never scrolls; `main` owns the scroll region. */
export const WorkstationLayout = () => {
  const principal = usePrincipalKey();
  const searchRef = useRef<SearchFieldHandle>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const { currentUser } = useUser();

  if (currentUser && isFrontDeskUser(currentUser)) return <FrontDeskRedirect />;

  return (
    <BrianBubbleProvider>
      <AnsweredCallNavigation />
      <div className="flex h-screen w-screen overflow-hidden bg-wall font-body text-ink-body">
        <WorkstationNav />
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-2 py-3 md:grid-cols-[minmax(0,1fr)_minmax(0,28rem)_minmax(0,1fr)] md:gap-4 md:px-6 md:py-4">
            <DisplayClock />
            <div className="col-span-2 row-start-2 md:col-span-1 md:row-auto">
              <SearchField ref={searchRef} />
            </div>
            <div className="col-start-2 row-start-1 flex min-w-0 items-center justify-end gap-2 md:col-start-3">
              <FailedUploadsBanner />
              <UserMenu onOpenShortcuts={() => setShortcutsOpen(true)} />
            </div>
          </header>
          <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto px-2 pb-24 md:px-6">
            <Outlet />
          </main>
        </div>
        <BrianCorner>
          <QuestionsBubble key={principal} />
          <CallBubble key={`call:${principal}`} />
        </BrianCorner>
      </div>
      <GlobalControls
        surface="workstation"
        onSearch={() => searchRef.current?.focus()}
        shortcutsOpen={shortcutsOpen}
        setShortcutsOpen={setShortcutsOpen}
      />
    </BrianBubbleProvider>
  );
};
